import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "./Wordmark";
import { BorderBeam } from "./border-beam";

type Props = {
  onCTA: () => void;
  onHome: () => void;
};

type Theme = "light" | "dark";
type ThemeWave = {
  x: number;
  y: number;
  radius: number;
  color: string;
};
type ViewTransitionResult = {
  finished?: Promise<void>;
};
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => ViewTransitionResult;
};

const THEME_KEY = "udi_theme";
const THEME_WAVE_MS = 760;

const LINKS = [
  ["section", "sobre", "Sobre"],
  ["page", "/metodo", "Método"],
  ["page", "/trilhas", "Trilhas"],
  ["section", "professor", "Professor"],
  ["section", "diagnostico", "Avaliação"],
  ["section", "faq", "FAQ"],
] as const;

function injectThemeTransition(origin: DOMRect | null) {
  const x = origin ? origin.left + origin.width / 2 : window.innerWidth - 48;
  const y = origin ? origin.top + origin.height / 2 : 48;
  const radius = Math.ceil(
    Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)),
  );
  const styleId = "udi-theme-transition";
  let style = document.getElementById(styleId) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    document.head.appendChild(style);
  }

  style.textContent = `
    ::view-transition-group(root) {
      animation-duration: .76s;
      animation-timing-function: cubic-bezier(.22, .61, .36, 1);
    }

    ::view-transition-old(root) {
      animation: none;
    }

    ::view-transition-new(root) {
      animation: udi-theme-reveal .76s cubic-bezier(.22, .61, .36, 1) both;
      clip-path: circle(0 at ${x}px ${y}px);
    }

    @keyframes udi-theme-reveal {
      from { clip-path: circle(0 at ${x}px ${y}px); }
      to { clip-path: circle(${radius}px at ${x}px ${y}px); }
    }
  `;
}

function getInitialTheme(): Theme {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return "light";
}

export function Nav({ onCTA, onHome }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [themeAnimating, setThemeAnimating] = useState(false);
  const [themeWave, setThemeWave] = useState<ThemeWave | null>(null);
  const themeButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const applyTheme = (nextTheme: Theme) => {
    document.documentElement.dataset.theme = nextTheme;
    localStorage.setItem(THEME_KEY, nextTheme);
    setTheme(nextTheme);
  };

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    const rect = themeButtonRef.current?.getBoundingClientRect() ?? null;

    setThemeAnimating(true);
    window.setTimeout(() => setThemeAnimating(false), THEME_WAVE_MS);

    const transitionDocument = document as ViewTransitionDocument;

    if (transitionDocument.startViewTransition) {
      injectThemeTransition(rect);
      transitionDocument.startViewTransition(() => applyTheme(nextTheme));
      return;
    }

    const x = rect ? rect.left + rect.width / 2 : window.innerWidth - 48;
    const y = rect ? rect.top + rect.height / 2 : 48;
    const radius = Math.ceil(
      Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)),
    );

    setThemeWave({
      x,
      y,
      radius,
      color: nextTheme === "dark" ? "#020a19" : "#fefcf8",
    });

    window.setTimeout(() => applyTheme(nextTheme), 240);
    window.setTimeout(() => setThemeWave(null), THEME_WAVE_MS);
  };

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const goSection = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      sessionStorage.setItem("udi_scroll_to", id);
      navigate("/");
      return;
    }
    scrollTo(id);
  };

  const goPage = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleLink = (kind: "section" | "page", target: string) => {
    if (kind === "page") goPage(target);
    else goSection(target);
  };

  return (
    <nav className={`nav ${scrolled ? "is-scrolled" : ""}`} aria-label="Principal">
      <div className="nav-pill">
        <Wordmark onClick={onHome} />
        <div className="nav-links">
          {LINKS.map(([kind, target, label]) => (
            <button key={target} className="nav-link" onClick={() => handleLink(kind, target)}>
              {label}
            </button>
          ))}
        </div>
        <button className="btn btn-primary nav-cta btn-beam btn-beam-primary" onClick={onCTA}>
          <BorderBeam />
          <span className="nav-cta-full">Avaliação Diagnóstica</span>
          <span className="nav-cta-short">Avaliação</span>
          <span className="arr">→</span>
        </button>
        <button
          ref={themeButtonRef}
          className={`nav-theme-btn${themeAnimating ? " is-switching" : ""}`}
          type="button"
          aria-label={theme === "dark" ? "Mudar para modo claro" : "Mudar para modo escuro"}
          aria-pressed={theme === "dark"}
          onClick={toggleTheme}
          title={theme === "dark" ? "Modo claro" : "Modo escuro"}
        >
          <svg className="nav-theme-icon nav-theme-sun" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
          </svg>
          <svg className="nav-theme-icon nav-theme-moon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 14.4A7.6 7.6 0 0 1 9.6 4a8.4 8.4 0 1 0 10.4 10.4Z" />
          </svg>
        </button>
        <button
          className="nav-menu-btn"
          type="button"
          aria-label="Abrir menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span />
          <span />
        </button>
      </div>

      {open && (
        <div className="nav-mobile-back" role="presentation" onClick={() => setOpen(false)}>
          <div
            className="nav-mobile-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Menu principal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nav-mobile-head">
              <Wordmark
                onClick={() => {
                  setOpen(false);
                  onHome();
                }}
              />
              <button
                className="nav-mobile-close"
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fechar menu"
              >
                ×
              </button>
            </div>
            <div className="nav-mobile-links">
              {LINKS.map(([kind, target, label]) => (
                <button key={target} type="button" onClick={() => handleLink(kind, target)}>
                  {label}
                </button>
              ))}
            </div>
            <button
              className="btn btn-primary btn-lg nav-mobile-cta btn-beam btn-beam-primary"
              onClick={() => {
                setOpen(false);
                onCTA();
              }}
            >
              <BorderBeam />
              Avaliação Diagnóstica <span className="arr">→</span>
            </button>
          </div>
        </div>
      )}
      {themeWave && (
        <span
          className="theme-wipe"
          aria-hidden="true"
          style={
            {
              "--theme-wave-x": `${themeWave.x}px`,
              "--theme-wave-y": `${themeWave.y}px`,
              "--theme-wave-radius": `${themeWave.radius}px`,
              backgroundColor: themeWave.color,
            } as CSSProperties
          }
        />
      )}
    </nav>
  );
}
